import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import OutsideClickHandler from 'react-outside-click-handler'
import ReactPortal from '../ReactPortal'
import useWindowScroll from '../../utils/useWindowScroll'
import useWindowSize from '../../utils/useWindowSize'
import styles from './Popover.module.scss'
import { Button, Grid, Typography } from '../index'
import Icon from '../Icon'

const SCREEN_PADDING = 30

const TRANSITION_DEFAULT_SETTINGS = {
  bottomSheet: {
    transitionDelay: {
      in: 0,
      out: 0,
    },
    transitionDuration: {
      in: 300,
      out: 300,
    },
    transitionTimingFunction: {
      in: 'ease',
      out: 'ease',
    },
    transitionProperty: {
      in: {
        transform: 'translateY(0%)',
      },
      out: {
        transform: 'translateY(100%)',
      },
    },
  },
  popover: {
    transitionDelay: {
      in: 0,
      out: 0,
    },
    transitionDuration: {
      in: 300,
      out: 300,
    },
    transitionTimingFunction: {
      in: 'ease',
      out: 'ease',
    },
    transitionProperty: {
      in: {
        opacity: 1,
        transform: 'scale(1)',
      },
      out: {
        opacity: 0,
        transform: 'scale(.5)',
      },
    },
  },
  custom: {
    transitionDelay: {
      in: 0,
      out: 0,
    },
    transitionDuration: {
      in: 300,
      out: 300,
    },
    transitionTimingFunction: {
      in: 'ease',
      out: 'ease',
    },
    transitionProperty: {
      in: {
        opacity: 1,
        transform: 'scale(1)',
      },
      out: {
        opacity: 0,
        transform: 'scale(.5)',
      },
    },
  },
  modal: {
    transitionDelay: {
      in: 0,
      out: 0,
    },
    transitionDuration: {
      in: 300,
      out: 300,
    },
    transitionTimingFunction: {
      in: 'ease',
      out: 'ease',
    },
    transitionProperty: {
      in: {
        opacity: 1,
        transform: 'scale(1)',
      },
      out: {
        opacity: 0,
        transform: 'scale(.5)',
      },
    },
  },
  backdrop: {
    transitionDelay: {
      in: 0,
      out: 0,
    },
    transitionDuration: {
      in: 300,
      out: 300,
    },
    transitionTimingFunction: {
      in: 'ease',
      out: 'ease',
    },
    transitionProperty: {
      in: {
        opacity: 1,
      },
      out: {
        opacity: 0,
      },
    },
  },
}

const BREAKPOINTS_DEFAULT = {
  0: {
    variant: 'bottomSheet',
    hasBackdrop: true,
    transition: { ...TRANSITION_DEFAULT_SETTINGS.bottomSheet },
    backdropTransition: { ...TRANSITION_DEFAULT_SETTINGS.backdrop },
  },
  768: {
    variant: 'popover',
    hasBackdrop: false,
    transition: { ...TRANSITION_DEFAULT_SETTINGS.popover },
    backdropTransition: { ...TRANSITION_DEFAULT_SETTINGS.backdrop },
  },
}

const Popover = (props) => {
  const {
    id,
    className = '',
    activeClassName = '',
    backdropClassName = '',
    backdropActiveClassName = '',
    headingClassName = '',
    innerClassName = '',
    innerActiveClassName = '',
    children = null,
    activeId = null,
    onClose = () => {},
    anchorEl = null,
    anchorPosition = null,
    breakpoints = BREAKPOINTS_DEFAULT,
    anchorOrigin: {
      vertical: anchorOriginVertical = 'bottom',
      horizontal: anchorOriginHorizontal = 'center',
    } = {},
    transformOrigin: {
      vertical: transformOriginVertical = 'top',
      horizontal: transformOriginHorizontal = 'center',
    } = {},
    label = null,
    ...rest
  } = props

  const popoverRef = useRef(null)
  const { y: scrollY, x: scrollX } = useWindowScroll(300)
  const { width: windowWidth, height: windowHeight } = useWindowSize(300)
  const [isShown, setShown] = useState(false)
  const [trigger, setTrigger] = useState(false)
  const [active, setActive] = useState(false)
  const rootElement = useRef()

  const [isMouseDown, setMouseDown] = useState(null)
  const [startPosY, setStartPosY] = useState(0)
  const [currentPosY, setCurrentPosY] = useState(false)

  const handleMouseDown = useCallback((e) => {
    setStartPosY(e.clientY || e.touches[0].clientY)
    setMouseDown(true)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isMouseDown) {
        return
      }
      const posY = (e.clientY || e.touches[0].clientY) - startPosY
      setCurrentPosY((posY > 0 ? posY : 0) + 'px')
    }

    const handleMouseUp = () => {
      if (!isMouseDown) {
        return
      }
      handleToggle(false)
      setMouseDown(false)
    }

    document.addEventListener('mouseup', handleMouseUp, false)
    document.addEventListener('touchend', handleMouseUp, false)
    document.addEventListener('mousemove', handleMouseMove, false)
    document.addEventListener('touchmove', handleMouseMove, false)

    return () => {
      document.removeEventListener('mouseup', handleMouseUp, false)
      document.removeEventListener('touchend', handleMouseUp, false)
      document.removeEventListener('mousemove', handleMouseMove, false)
      document.removeEventListener('touchmove', handleMouseMove, false)
    }
  }, [startPosY, setMouseDown, setCurrentPosY, isMouseDown])

  useEffect(() => {
    if (isMouseDown === false) {
      setCurrentPosY('100%')
    }
  }, [isMouseDown])

  const handleToggle = useCallback((state) => {
    setActive(state)

    if (state) {
      setShown(true)
    }
  }, [])

  useEffect(() => {
    setTrigger(active)
  }, [active])

  const handleTransitionEnd = useCallback(() => {
    if (isMouseDown) {
      return
    }
    if (!trigger && isShown) {
      setShown(false)
      onClose()
    }
  }, [trigger, isShown, onClose, isMouseDown])

  const direction = useMemo(() => (trigger ? 'in' : 'out'), [trigger])

  const settings = useMemo(() => {
    if (!breakpoints) {
      return rest
    }

    const breakpointsArr = Object.entries(
      breakpoints === true ? BREAKPOINTS_DEFAULT : breakpoints,
    )

    let matchBreakpoints = null

    breakpointsArr.forEach(([k, v]) => {
      if (windowWidth >= +k) {
        matchBreakpoints = v
      }
    })

    return matchBreakpoints || rest
  }, [breakpoints, rest, windowWidth])

  const {
    transition,
    backdropTransition = { ...TRANSITION_DEFAULT_SETTINGS.backdrop },
    hasBackdrop = false,
    variant = 'popover',
    showLabel = false,
    ...other_settings
  } = useMemo(
    () => ({
      ...settings,
      transition: settings?.transition ?? {
        ...TRANSITION_DEFAULT_SETTINGS[settings.variant],
      },
    }),
    [settings],
  )

  useEffect(() => {
    rootElement.current = document.getElementById('react-root') || document.body
  }, [])

  const getParents = useCallback((elem) => {
    // Set up a parent array
    const parents = []

    // Push each parent element to the array
    for (; elem && elem !== document; elem = elem.parentNode) {
      parents.push(elem)
    }

    // Return our parent array
    return parents
  }, [])

  const zIndex = useMemo(() => {
    const zIndex = {}

    if (!anchorEl || !variant === 'popover') {
      return zIndex
    }

    const parents = getParents(anchorEl)

    parents.forEach((ele) => {
      const z = window.getComputedStyle(ele).getPropertyValue('z-index')
      if (typeof +z === 'number') {
        if (!zIndex.zIndex) {
          zIndex.zIndex = 1
        }
        if (+z > zIndex.zIndex) {
          zIndex.zIndex = z
        }
      }
    })
    return zIndex
  }, [anchorEl])

  const popoverCSS = useMemo(() => {
    const {
      top: anchorElTop = 0,
      width: anchorElWidth = 0,
      height: anchorElHeight = 0,
      left: anchorElLeft = 0,
    } = (
      anchorEl || document.querySelector(`[data-trigger-id="${id}"]`)
    )?.getBoundingClientRect() ?? {}

    const { offsetWidth: popoverWidth = 0, offsetHeight: popoverHeight = 0 } =
      popoverRef.current ?? {}

    let top, left

    if (Number.isInteger(anchorOriginVertical)) {
      top = anchorOriginVertical
    } else {
      switch (anchorOriginVertical) {
        case 'top': {
          top = anchorElTop
          break
        }
        case 'center': {
          top = anchorElTop + anchorElHeight / 2
          break
        }
        case 'bottom':
        default: {
          top = anchorElTop + anchorElHeight
          break
        }
      }
    }

    if (Number.isInteger(transformOriginVertical)) {
      top = top + transformOriginVertical
    } else {
      switch (transformOriginVertical) {
        case 'bottom': {
          top = top - popoverHeight
          break
        }
        case 'center': {
          top = top - popoverHeight / 2
          break
        }
        case 'top':
        default: {
          break
        }
      }
    }

    if (Number.isInteger(anchorOriginHorizontal)) {
      left = anchorOriginHorizontal
    } else {
      switch (anchorOriginHorizontal) {
        case 'right': {
          left = anchorElLeft + anchorElWidth
          break
        }
        case 'center': {
          left = anchorElLeft + anchorElWidth / 2
          break
        }
        case 'left':
        default: {
          left = anchorElLeft
          break
        }
      }
    }

    if (Number.isInteger(transformOriginHorizontal)) {
      left = left + transformOriginHorizontal
    } else {
      switch (transformOriginHorizontal) {
        case 'right': {
          left = left - popoverWidth
          break
        }
        case 'center': {
          left = left - popoverWidth / 2
          break
        }
        case 'left':
        default: {
          break
        }
      }
    }

    const transitionCSS =
      isMouseDown === true
        ? { transform: `translateY(${currentPosY})`, transition: 'none' }
        : {
            ...transition.transitionProperty[direction],
            transitionProperty: Object.keys(
              transition.transitionProperty[direction],
            ).join(','),
            transitionTimingFunction:
              transition.transitionTimingFunction[direction],
            transitionDuration: transition.transitionDuration[direction] + 'ms',
            transitionDelay: transition.transitionDelay[direction] + 'ms',
          }

    let css = {}

    switch (variant) {
      case 'bottomSheet': {
        css = {
          bottom: 0,
          left: 0,
          right: 0,
          transformOrigin: '0 0',
          ...transitionCSS,
        }
        break
      }
      case 'modal':
      case 'custom': {
        css = {
          ...transitionCSS,
        }
        break
      }
      default:
      case 'popover': {
        left =
          left + popoverWidth > windowWidth
            ? windowWidth - popoverWidth - SCREEN_PADDING
            : left

        top =
          top + popoverHeight > windowHeight - SCREEN_PADDING &&
          anchorElTop - popoverHeight >= SCREEN_PADDING
            ? anchorElTop -
              popoverHeight -
              (Number.isInteger(transformOriginVertical)
                ? transformOriginVertical
                : 0)
            : top

        css = {
          top: top + scrollY,
          left: left < 0 ? SCREEN_PADDING : left,
          transformOrigin: `${
            isNaN(transformOriginHorizontal)
              ? transformOriginHorizontal
              : transformOriginHorizontal + 'px'
          } ${
            isNaN(transformOriginVertical)
              ? transformOriginVertical
              : transformOriginVertical + 'px'
          }`,
          ...transitionCSS,
        }
        break
      }
    }

    return { ...css, ...anchorPosition }
  }, [
    anchorEl,
    anchorPosition,
    popoverRef,
    transformOriginVertical,
    transformOriginHorizontal,
    anchorOriginHorizontal,
    anchorOriginVertical,
    variant,
    direction,
    transition,
    trigger,
    rootElement,
    windowWidth,
    windowHeight,
    scrollY,
    scrollX,
    currentPosY,
    isMouseDown,
  ])

  useEffect(() => {
    const currentRootElement = rootElement.current

    if (!currentRootElement) {
      return
    }

    if (!popoverRef.current) {
      currentRootElement.style.minHeight = ''
      return
    }

    const { offsetHeight: popoverHeight = 0 } = popoverRef.current
    const { top = 0 } = popoverCSS

    if (top + popoverHeight > windowHeight + scrollY) {
      currentRootElement.style.minHeight = top + popoverHeight + 'px'
    }
  }, [popoverRef.current])

  const backdropCSS = useMemo(
    () => ({
      ...backdropTransition.transitionProperty[direction],
      transitionProperty: Object.keys(
        backdropTransition.transitionProperty[direction],
      ).join(','),
      transitionTimingFunction:
        backdropTransition.transitionTimingFunction[direction],
      transitionDuration:
        backdropTransition.transitionDuration[direction] + 'ms',
      transitionDelay: backdropTransition.transitionDelay[direction] + 'ms',
    }),
    [backdropTransition, direction],
  )

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (activeId && activeId === id && event.key === 'Escape') {
        event.preventDefault()
        handleToggle(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeId, id])

  useEffect(() => {
    handleToggle(!!(activeId && activeId === id))
  }, [activeId, id])

  const handleOutsideClick = useCallback(() => {
    if (isShown) {
      handleToggle(false)
    }
  }, [isShown])

  const scrollbarWidth = useMemo(() => {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll'
    outer.style.msOverflowStyle = 'scrollbar'
    document.body.appendChild(outer)

    const inner = document.createElement('div')
    outer.appendChild(inner)

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    outer.parentNode.removeChild(outer)

    return scrollbarWidth
  }, [windowWidth])

  useEffect(() => {
    if (!hasBackdrop) {
      return
    }
    document.documentElement.style.overflow = isShown ? 'hidden' : ''
    document.body.style.paddingRight = isShown ? `${scrollbarWidth}px` : ''
  }, [hasBackdrop, isShown, scrollbarWidth])

  const popoverLabel = useMemo(
    () => label ?? other_settings.label,
    [other_settings.label, label],
  )

  if (!isShown) {
    return null
  }

  return (
    <ReactPortal wrapperId="react-portal-popover-container">
      <div
        className={clsx(
          styles.popover,
          className,
          styles[`popover_variant-${variant}`],
          { [activeClassName]: trigger },
        )}
        style={{ ...zIndex }}
      >
        {hasBackdrop && (
          <div
            className={clsx(styles.popover__backdrop, backdropClassName, {
              [backdropActiveClassName]: trigger,
            })}
            style={backdropCSS}
          />
        )}
        <OutsideClickHandler onOutsideClick={handleOutsideClick}>
          <div
            ref={popoverRef}
            className={clsx(
              styles.popover__inner,
              `popover__inner_variant-${variant}`,
              styles[`popover__inner_variant-${variant}`],
              { [innerActiveClassName]: trigger },
            )}
            style={popoverCSS}
            onTransitionEnd={handleTransitionEnd}
          >
            {variant === 'bottomSheet' && (
              <button
                className={styles['popover__close-line']}
                onMouseDown={handleMouseDown}
                onTouchStart={handleMouseDown}
              />
            )}
            <div className={clsx(styles['popover__body'], innerClassName)}>
              {showLabel && popoverLabel && (
                <Grid
                  wrap={false}
                  justifyContent={'between'}
                  alignItems={'start'}
                  className={clsx(styles['popover__heading'], headingClassName)}
                >
                  <Grid alignSelf={'center'}>
                    <Typography
                      component={
                        typeof popoverLabel === 'string' ? 'h4' : popoverLabel
                      }
                      variant={
                        variant === 'bottomSheet' ? 'Body-20' : 'Header-50'
                      }
                    >
                      {popoverLabel}
                    </Typography>
                  </Grid>
                  <Button
                    noLabel
                    startIcon={<Icon name={'close'} />}
                    size={variant === 'bottomSheet' ? 'm' : 's'}
                    onClick={onClose}
                  />
                </Grid>
              )}

              {children}
            </div>
          </div>
        </OutsideClickHandler>
      </div>
    </ReactPortal>
  )
}

export default Popover

Popover.propTypes = {
  anchorEl: PropTypes.oneOfType([
    PropTypes.instanceOf(Element),
    PropTypes.oneOf([null]),
  ]),
  className: PropTypes.string,
  activeClassName: PropTypes.string,
  innerClassName: PropTypes.string,
  innerActiveClassName: PropTypes.string,
  backdropClassName: PropTypes.string,
  backdropActiveClassName: PropTypes.string,
  headingClassName: PropTypes.string,
  id: PropTypes.string,
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  onClose: PropTypes.func,
  hasBackdrop: PropTypes.bool,
  label: PropTypes.oneOfType([null, PropTypes.string]),
  anchorPosition: PropTypes.oneOfType([
    PropTypes.shape({
      top: PropTypes.number,
      left: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
    }),
    PropTypes.oneOf([null]),
  ]),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOfType([
      PropTypes.oneOf(['top', 'center', 'bottom']),
      PropTypes.number,
    ]),

    horizontal: PropTypes.oneOfType([
      PropTypes.oneOf(['left', 'center', 'right']),
      PropTypes.number,
    ]),
  }),
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]),
  variant: PropTypes.oneOf(['popover', 'bottomSheet', 'custom', 'modal']),
  breakpoints: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  disabled: PropTypes.bool,
}
